/**
 * ContactTables — Top senders and top recipients tables.
 */

import React from 'react';
import { Table, Typography, Avatar } from 'antd';
import { motion, useReducedMotion } from 'framer-motion';
import { Users, Send } from 'lucide-react';
import { FADE_UP } from '../constants';

const { Text } = Typography;

function getInitials(email = '') {
  const local = email.split('@')[0];
  return local.slice(0, 2).toUpperCase();
}

function avatarColor(email) {
  const colors = ['#6366f1', '#22c55e', '#f59e0b', '#3b82f6', '#ef4444', '#8b5cf6', '#ec4899'];
  let hash = 0;
  for (const c of email) hash = c.charCodeAt(0) + hash * 31;
  return colors[Math.abs(hash) % colors.length];
}

function cardStyle(isDark) {
  return {
    borderRadius: 14,
    background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
    boxShadow: isDark
      ? '0 1px 3px rgba(0,0,0,0.2)'
      : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    padding: '20px',
    height: '100%',
  };
}

const columns = (maxCount, isDark, accentColor) => [
  {
    title: 'Contact',
    dataIndex: 'email',
    key: 'email',
    render: (email) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar size={30} style={{ background: avatarColor(email), fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
          {getInitials(email)}
        </Avatar>
        <Text ellipsis style={{ maxWidth: 220, fontSize: 13 }}>{email}</Text>
      </div>
    ),
  },
  {
    title: 'Emails',
    dataIndex: 'count',
    key: 'count',
    width: 160,
    align: 'right',
    render: (count) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
        <div style={{
          height: 6,
          borderRadius: 3,
          background: accentColor,
          opacity: 0.7,
          width: `${Math.round((count / maxCount) * 80)}px`,
          minWidth: 4,
          flexShrink: 0,
        }} />
        <Text strong style={{ minWidth: 28, textAlign: 'right', fontSize: 13 }}>{count}</Text>
      </div>
    ),
  },
];

export function ContactTables({ metrics, isDark }) {
  const reduced = useReducedMotion();
  const anim = reduced ? {} : FADE_UP;

  const maxSender = metrics.topSenders[0]?.count || 1;
  const maxRecipient = metrics.topRecipients[0]?.count || 1;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
      {/* Top senders */}
      <motion.div variants={anim} initial="hidden" animate="visible">
        <div style={cardStyle(isDark)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Users size={16} color="#6366f1" strokeWidth={2} />
            </div>
            <Text strong style={{ fontSize: 14 }}>Top Senders</Text>
          </div>
          {metrics.topSenders.length === 0 ? (
            <Text type="secondary">No sender data</Text>
          ) : (
            <Table
              dataSource={metrics.topSenders.map((s, i) => ({ ...s, key: i }))}
              columns={columns(maxSender, isDark, '#6366f1')}
              pagination={false}
              size="small"
              showHeader={false}
            />
          )}
        </div>
      </motion.div>

      {/* Top recipients */}
      <motion.div variants={anim} initial="hidden" animate="visible">
        <div style={cardStyle(isDark)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.08)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Send size={16} color="#10b981" strokeWidth={2} />
            </div>
            <Text strong style={{ fontSize: 14 }}>Top Recipients</Text>
          </div>
          {metrics.topRecipients.length === 0 ? (
            <Text type="secondary">No recipient data</Text>
          ) : (
            <Table
              dataSource={metrics.topRecipients.map((r, i) => ({ ...r, key: i }))}
              columns={columns(maxRecipient, isDark, '#10b981')}
              pagination={false}
              size="small"
              showHeader={false}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
}
